import { useContext } from 'react';
import { useGetIarAgencyMembersQuery } from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import type {
  IarAgency,
  IarMember,
} from '@rsos/reduxDataStore/src/features/eventShare/types';
import type { IarSelectedMember } from './helpers';
import { IarMockMembersContext } from './iarMembersContext';

/**
 * Lowercases, strips accents, and drops punctuation so search is forgiving of
 * how the dispatcher types a name (commas, hyphens, apostrophes, accents).
 * e.g. "José", "O'Brien", "Smith-Jones" → "jose", "obrien", "smithjones".
 */
const normalizeForSearch = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip Latin diacritics
    // Strip the four ASCII punctuation/symbol blocks so letters of ANY script
    // are preserved \u2014 non-Latin names (Cyrillic, CJK, Arabic) stay searchable.
    // Avoids the \p{L} unicode class, which needs an ES2018 regex target this
    // project doesn't compile against.
    .replace(/[!-/:-@[-`{-~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Case-insensitive, order-independent, punctuation/accent-forgiving search.
 * The term is split into tokens and every token must appear somewhere in the
 * member's combined first/last/position text, so any order works:
 * "smith john", "john smith", "captain smith", and "Smith, John" all match.
 */
export const matchesSearch = (member: IarMember, term: string): boolean => {
  const tokens = normalizeForSearch(term).split(' ').filter(Boolean);
  if (tokens.length === 0) return true;
  const haystack = normalizeForSearch(
    `${member.firstName} ${member.lastName} ${member.position || ''}`,
  );
  return tokens.every(token => haystack.includes(token));
};

/** Builds the IarSelectedMember payload sent on the create-session POST. */
export const buildSelection = (
  member: IarMember,
  agency: IarAgency,
): IarSelectedMember => ({
  agency_id: agency.legacyId,
  member_id: member.legacyId,
  name: `${member.firstName} ${member.lastName}`,
  subscriber_name: agency.name,
});

/**
 * Resolves the member list for an agency. If a mock map is supplied via
 * IarMockMembersContext (stories/tests), returns that synchronously. Otherwise
 * delegates to the live RTK Query call. The query is always invoked to obey
 * the rules of hooks but is skipped when mock data is present.
 */
export const useResolvedMembers = (org: string, agencyId: number) => {
  const mockMap = useContext(IarMockMembersContext);
  const mockMembers = mockMap?.[agencyId];
  const queryResult = useGetIarAgencyMembersQuery(
    { org, agencyId },
    { skip: mockMembers != null },
  );
  if (mockMembers) {
    return { data: mockMembers, isLoading: false, isError: false };
  }
  return queryResult;
};

/** Returns true if any agency member matches the search term. */
export const useAgencyHasMatches = (
  org: string,
  agencyId: number,
  searchTerm: string,
): boolean => {
  const { data: members } = useResolvedMembers(org, agencyId);
  if (!searchTerm) return true;
  return (members ?? []).some(m => matchesSearch(m, searchTerm));
};
